/**
 * Phone numbers as they travel between the app and giving-api: `+<calling code>
 * <national number>`, one space, otherwise digits only, no trunk 0 — `+65 91234567`.
 * giving-api and the MyGiving web portal use the same format.
 */
import {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import { withLeadingPlus } from '@/utils/country-code';

export const DEFAULT_DIAL_CODE = '65';

const digitsOf = value => String(value ?? '').replace(/\D/g, '');

// "+65", "65", "++65", " + 6 5 " → "65"; empty when there is no code.
const bareDialCode = code => withLeadingPlus(digitsOf(code)).slice(1);

/** The country a calling code belongs to: `65` → `SG`, `1` → `US`, `44` → `GB`. */
export function iso2ForDialCode(dialCode) {
  return metadata.country_calling_codes[bareDialCode(dialCode)]?.[0] || '';
}

// The national number with its trunk prefix dropped, as the country dials it
// (Italy keeps its 0). A number too short for libphonenumber loses a leading 0.
function nationalDigits(dialCode, number) {
  const digits = digitsOf(number);
  if (!digits) return '';
  const parsed = parsePhoneNumberFromString(digits, { defaultCallingCode: dialCode });
  if (parsed?.nationalNumber) return String(parsed.nationalNumber);
  return digits.replace(/^0/, '');
}

/** `('65', '9123 4567')` → `+65 91234567`. Empty without a number. */
export function formatContactNumber(dialCode, nationalNumber) {
  const code = bareDialCode(dialCode);
  if (!code) return digitsOf(nationalNumber);
  const national = nationalDigits(code, nationalNumber);
  return national ? `+${code} ${national}` : '';
}

/**
 * A stored number as its parts: `+65 91234567`, `+6591234567`, `++65 9123 4567`
 * all give `{ dialCode: '65', iso2: 'SG', nationalNumber: '91234567' }`. A number
 * with no calling code takes `fallbackDialCode`; a number still being typed
 * (`+65 9123`) keeps what it has.
 */
export function splitContactNumber(stored, fallbackDialCode = DEFAULT_DIAL_CODE) {
  const text = String(stored ?? '').trim();
  const fallback = bareDialCode(fallbackDialCode) || DEFAULT_DIAL_CODE;

  if (!text.startsWith('+')) {
    return {
      dialCode: fallback,
      iso2: iso2ForDialCode(fallback),
      nationalNumber: nationalDigits(fallback, text),
    };
  }

  const international = `+${text.replace(/^\++/, '')}`;
  // The canonical form says where the code ends; trust it over a guess.
  const spaced = international.match(/^\+\s*(\d{1,3})(?:\s+(.*))?$/);
  if (spaced && iso2ForDialCode(spaced[1])) {
    const [, dialCode, rest] = spaced;
    return {
      dialCode,
      iso2: parsed(international)?.country || iso2ForDialCode(dialCode),
      nationalNumber: nationalDigits(dialCode, rest),
    };
  }

  const phone = parsed(international);
  if (phone) {
    return {
      dialCode: String(phone.countryCallingCode),
      iso2: phone.country || iso2ForDialCode(phone.countryCallingCode),
      nationalNumber: String(phone.nationalNumber),
    };
  }
  return { dialCode: fallback, iso2: iso2ForDialCode(fallback), nationalNumber: '' };
}

function parsed(international) {
  return parsePhoneNumberFromString(`+${digitsOf(international)}`);
}

/** Any stored form as the canonical one: `++65 9123 4567` → `+65 91234567`. */
export function normalizeContactNumber(stored, fallbackDialCode = DEFAULT_DIAL_CODE) {
  const { dialCode, nationalNumber } = splitContactNumber(stored, fallbackDialCode);
  return formatContactNumber(dialCode, nationalNumber);
}

/**
 * Whether a national number is a real number for the country (`SG`) or calling
 * code (`65`, `+65`): an SG one is 8 digits starting 3, 6, 8 or 9.
 */
export function isValidContactNumber(countryOrDialCode, nationalNumber) {
  const digits = digitsOf(nationalNumber);
  const key = String(countryOrDialCode ?? '').trim();
  if (!digits || !key) return false;
  if (/^[A-Za-z]{2}$/.test(key)) return isValidPhoneNumber(digits, key.toUpperCase());
  const code = bareDialCode(key);
  if (!code) return false;
  const phone = parsePhoneNumberFromString(digits, { defaultCallingCode: code });
  return Boolean(phone?.isValid() && phone.countryCallingCode === code);
}

/** The calling code of a country: `SG` → `65`. Empty for one it does not know. */
export function dialCodeForCountry(iso2) {
  try {
    return getCountryCallingCode(String(iso2 ?? '').toUpperCase());
  } catch {
    return '';
  }
}
